import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Download, ChevronRight, Check, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const IMAGES = {
  products: "https://static.prod-images.emergentagent.com/jobs/d59d775d-5692-4c00-811c-246ab4499e3b/images/13b23b80958d26633e19e3997b20dfef9c228f9b1fe02a49a3ad41be5a2f5618.png",
  factory: "https://static.prod-images.emergentagent.com/jobs/d59d775d-5692-4c00-811c-246ab4499e3b/images/bb772e928b0a24500ef8797bceac569ca367a873549bf837f33b9ec01aa31b05.png",
};

const PageHeader = () => {
  return (
    <section className="pt-32 pb-20 bg-[#0e1c3f] relative overflow-hidden" data-testid="produits-header">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#0078b7] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#ff65c3] rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-3xl">
          <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">
            Nos Solutions
          </p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-6">
            Nos <span className="text-[#ff65c3]">Produits</span>
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed">
            Découvrez notre gamme complète d'éléments en béton précontraint, 
            conçus pour répondre aux exigences les plus strictes de la construction moderne.
          </p>
        </div>
      </div>
    </section>
  );
};

const ProductShowcase = () => {
  const [products, setProducts] = useState([]);
  const [activeProduct, setActiveProduct] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/api/products`);
        setProducts(data);
      } catch (e) {
        console.error('Error fetching products:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-[600px] bg-slate-100 animate-pulse" />
        </div>
      </section>
    );
  }

  if (products.length === 0) return null;

  const currentProduct = products[activeProduct];

  return (
    <section className="py-0 bg-white" data-testid="products-showcase">
      {/* Main Product Display */}
      <div className="relative">
        {/* Product Image Section */}
        <div className="relative h-[55vh] sm:h-[65vh] lg:h-[70vh] min-h-[400px] sm:min-h-[500px] lg:min-h-[600px] overflow-hidden">
          {products.map((product, index) => (
            <div
              key={product.id}
              className={`absolute inset-0 transition-all duration-700 ease-out ${
                index === activeProduct 
                  ? 'opacity-100 scale-100' 
                  : 'opacity-0 scale-105'
              }`}
            >
              <img 
                src={product.image_url || IMAGES.products}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0e1c3f] via-[#0e1c3f]/70 to-transparent" />
            </div>
          ))}

          {/* Content Overlay */}
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Product Info */}
                <div className="max-w-xl">
                  <div className="flex items-center gap-4 mb-4 sm:mb-8">
                    <span className="text-[#ff65c3] font-heading font-black text-4xl sm:text-5xl lg:text-7xl opacity-50">
                      {String(activeProduct + 1).padStart(2, '0')}
                    </span>
                    <div className="h-px flex-1 bg-gradient-to-r from-[#ff65c3] to-transparent max-w-32" />
                  </div>
                  
                  <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-white uppercase mb-3 sm:mb-6 tracking-tight">
                    {currentProduct.name}
                  </h2>
                  
                  <p className="text-slate-300 text-sm sm:text-base lg:text-lg mb-4 sm:mb-8 leading-relaxed line-clamp-3 sm:line-clamp-none">
                    {currentProduct.short_description}
                  </p>

                  {/* Features Pills */}
                  {currentProduct.features && currentProduct.features.length > 0 && (
                    <div className="hidden sm:flex flex-wrap gap-3 mb-10">
                      {currentProduct.features.slice(0, 4).map((feature, i) => (
                        <span 
                          key={i}
                          className="px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-sm rounded-full border border-white/20 flex items-center gap-2"
                        >
                          <Check className="h-4 w-4 text-[#0078b7]" />
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* CTA Buttons */}
                  <div className="flex flex-wrap gap-4">
                    <Button 
                      asChild 
                      size="lg"
                      className="bg-[#0078b7] hover:bg-[#0078b7]/90 text-white rounded-sm"
                    >
                      <Link to={`/produits/${currentProduct.slug}`}>
                        Découvrir
                        <ArrowUpRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                    {currentProduct.pdf_url && (
                      <Button 
                        asChild 
                        size="lg"
                        variant="outline"
                        className="border-[#ff65c3] text-[#ff65c3] hover:bg-[#ff65c3]/10 rounded-sm"
                      >
                        <a href={currentProduct.pdf_url} target="_blank" rel="noopener noreferrer">
                          <Download className="mr-2 h-5 w-5" />
                          Fiche technique
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Navigation - Fixed at bottom */}
        <div className="bg-[#0e1c3f]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4">
              {products.map((product, index) => (
                <button
                  key={product.id}
                  onClick={() => setActiveProduct(index)}
                  className={`relative p-6 md:p-8 text-left transition-all duration-300 border-b-4 group ${
                    index === activeProduct 
                      ? 'bg-[#0078b7]/20 border-[#0078b7]' 
                      : 'bg-transparent border-transparent hover:bg-white/5 hover:border-[#ff65c3]'
                  }`}
                  data-testid={`product-nav-${product.slug}`}
                >
                  <span className={`block font-heading font-black text-2xl mb-2 transition-colors ${
                    index === activeProduct ? 'text-[#0078b7]' : 'text-[#ff65c3]/50 group-hover:text-[#ff65c3]'
                  }`}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className={`block font-heading font-bold text-sm md:text-base transition-colors ${
                    index === activeProduct ? 'text-white' : 'text-white/60 group-hover:text-white'
                  }`}>
                    {product.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ProductsGrid = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/api/products`);
        setProducts(data);
      } catch (e) {
        console.error('Error fetching products:', e);
      }
    };
    fetchProducts();
  }, []);

  return (
    <section className="py-24 bg-slate-50" data-testid="products-grid">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">
            Catalogue complet
          </p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight">
            Explorez nos solutions
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {products.map((product, index) => (
            <div 
              key={product.id}
              className="group relative bg-white overflow-hidden"
              data-testid={`product-card-${product.slug}`}
            >
              <Link to={`/produits/${product.slug}`} className="block">
                {/* Image */}
                <div className="aspect-[16/9] overflow-hidden">
                  <img 
                    src={product.image_url || IMAGES.products} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e1c3f] via-[#0e1c3f]/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-[#ff65c3] font-heading font-black text-4xl opacity-50">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="h-px flex-1 bg-gradient-to-r from-[#ff65c3] to-transparent max-w-20" />
                  </div>
                  
                  <h3 className="font-heading font-bold text-2xl text-white mb-3 group-hover:text-[#0078b7] transition-colors">
                    {product.name}
                  </h3>
                  
                  <p className="text-white/70 text-sm mb-4 line-clamp-2">
                    {product.short_description}
                  </p>

                  <div className="flex items-center gap-4">
                    <span className="text-[#0078b7] text-sm font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                      Découvrir
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>

              {/* PDF download button - outside the Link to prevent navigation */}
              {product.pdf_url && (
                <a
                  href={product.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-4 right-4 z-10 flex items-center gap-2 px-3 py-1.5 bg-[#ff65c3] text-white text-xs font-bold rounded-sm hover:bg-[#ff65c3]/80 transition-colors shadow-lg"
                  data-testid={`pdf-btn-${product.slug}`}
                >
                  <Download className="h-3.5 w-3.5" />
                  Fiche technique
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  return (
    <section className="py-24 bg-[#0078b7] relative overflow-hidden" data-testid="produits-cta">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#ff65c3] rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
      </div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-white uppercase tracking-tight mb-6">
          Besoin d'un conseil technique ?
        </h2>
        <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
          Notre bureau d'études est à votre disposition pour vous accompagner 
          dans le choix et le dimensionnement de vos produits.
        </p>
        <Button 
          asChild 
          size="lg" 
          className="bg-white text-[#0078b7] hover:bg-slate-100 font-medium px-8 rounded-sm"
        >
          <Link to="/contact">
            Demander un devis
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </Button>
      </div>
    </section>
  );
};

const ProduitsPage = () => {
  return (
    <div data-testid="produits-page">
      <PageHeader />
      <ProductShowcase />
      <ProductsGrid />
      <CTASection />
    </div>
  );
};

export default ProduitsPage;
